import { Observable, of } from 'rxjs';

import {
  Container,
  Service,
  AuthService,
  IStreakDTO,
  LearningExperienceType,
  ILeaderboard,
} from '../../../../symphony';

import { SlLEContext } from '../../global-constants';
import { IActionData } from '../../global-interface';
import { SlEventsService } from '../Business/sl-events.service';
import { SlCommonService } from '../Business/sl-common.service';
import { SlCourseService } from '../Business/sl-course.service';
import { SlLanguageService } from '../Business/sl-language.service';
import { SlMaterialService } from '../Business/sl-material.service';
import { SlLessonService } from '../Business/sl-lesson.service';
import { SlHeartsService } from '../Business/sl-hearts.service';
import { SlTrackingService } from '../Business/sl-tracking.service';
import { SlLeaderboardService } from '../Business/sl-leaderboard.service';
import { SlExperimentsService } from '../Business/sl-experiments.service';
import { SlStreakService } from '../Business/sl-streak.service';
import { IExperimentsDataResponse } from '../../../../api/private/global.interface';
import { ILandingPageResponse } from '../../../../api/private/learn-engine-api/landing-page-api.interface';
import {
  IMaterial, ILearningPathSubtree, ILessonSubtree, ISolveMaterialDTO, IScreenConfig, ICategorizedCourse, IMaterialSolutionDTO,
} from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { ICertificate } from '../../../../api/public/certificates-api';
import { IKudosResponse, IKudosHeartConfigsResponse } from '../../../../api/public/kudos-api';
import { LanguagesResponseType } from '../../../../api/public/static-pages-api';
import { IUserSettingsResponse } from '../../../../api/public/user-settings-api';

@Service()
export class SlActionsController {
  private commonService = Container.take(SlLEContext, SlCommonService);

  private eventsService = Container.take(SlLEContext, SlEventsService);

  private courseService = Container.take(SlLEContext, SlCourseService);

  private languageService = Container.take(SlLEContext, SlLanguageService);

  private lessonService = Container.take(SlLEContext, SlLessonService);

  private materialService = Container.take(SlLEContext, SlMaterialService);

  private heartsService = Container.take(SlLEContext, SlHeartsService);

  private streakService = Container.take(SlLEContext, SlStreakService);

  private experimentsService = Container.take(
    SlLEContext,
    SlExperimentsService,
  );

  private leaderboardService = Container.take(
    SlLEContext,
    SlLeaderboardService,
  );

  private trackingService = Container.take(SlLEContext, SlTrackingService);

  private authService = Container.take('global', AuthService);

  // Network communication

  public getCourseSubtreeHandler = ({
    alias,
  }: IActionData): Observable<IMaterial> => this.courseService.getCourseSubtree(alias);

  public getLearningPathSubtreeHandler = ({
    alias,
  }: IActionData): Observable<ILearningPathSubtree> => this.courseService.getLearningPathSubtree(alias);

  public getCertificateHandler = ({
    alias,
  }: IActionData): Observable<ICertificate> => this.courseService.getCertificate(alias);

  public setLessonDataHandler = ({
    id,
    lessonPageIndex,
    isTranslating,
  }: IActionData): Observable<ILessonSubtree> => this.lessonService.setLessonData(
    id,
    lessonPageIndex,
    isTranslating,
  );

  // TODO: change after implements LearningPath
  public solveMaterialHandler = ({
    solutions,
    id,
  }: IActionData): Observable<ISolveMaterialDTO> => this.materialService.solveMaterial({
    solutions,
    experience_type_id: LearningExperienceType.Course,
    bundleId: id,
  });

  public enrollCourseHandler = ({
    alias,
    type,
  }: IActionData): Observable<null> => this.courseService.enrollCourse(alias, type);

  public getLandingPageDataHandler = ({
    alias,
  }: IActionData): Observable<ILandingPageResponse> => this.commonService.getLandingPageData(alias);

  public refillFreeHeartsHandler = (): Observable<IKudosResponse> => this.heartsService.refillFreeHearts();

  public getHeartConfigsHandler = (): Observable<IKudosHeartConfigsResponse> => this.heartsService.getHeartConfigs();

  public getLessonCompleteScreensHandler = (): Observable<
    IScreenConfig[]
  > => this.lessonService.getLessonCompleteScreens();

  public getLeaderboardPredictedStateHandler = (): Observable<ILeaderboard> => this.leaderboardService.getLeaderboardPredictedState();

  public getExperimentsHandler = (): Observable<IExperimentsDataResponse> => this.experimentsService.getExperiments();

  public getCourseCategoriesHandler = (): Observable<
    ICategorizedCourse[]
  > => this.courseService.getCourseCategories();

  public getProgrammingLanguagesHandler = (): Observable<LanguagesResponseType> => this.languageService.getProgrammingLanguages();

  public getUserSettingsHandler = (): Observable<IUserSettingsResponse> => this.commonService.getUserSettings();

  public updateUserSettingsHandler = ({
    userSetting,
  }: IActionData): Observable<IUserSettingsResponse> => this.commonService.updateUserSettings(userSetting);

  public setStreakGoalHandler = ({
    numberValue,
  }: IActionData): Observable<null> => this.streakService.setStreakGoal(numberValue);

  // Business actions

  public initializeAppHandler = (): Observable<null> => this.commonService.initializeApp();

  public startLessonSessionDataRecordingHandler = (): Observable<null> => this.lessonService.startLessonSessionDataRecording();

  public removeLessonSessionDataRecodingHandler = (): Observable<null> => this.lessonService.removeLessonSessionDataRecording();

  public cleanupCourseHandler = ({
    alias,
  }: IActionData): Observable<null> => this.courseService.cleanupCourse(alias);

  public cleanupPathHandler = ({ alias }: IActionData): Observable<null> => this.courseService.cleanupPath(alias);

  public navigateToCourseHandler = (): Observable<null> => this.courseService.navigateToCourse();

  public getCourseHandler = ({ alias }: IActionData): Observable<null> => this.courseService.getCourse(alias);

  public getLearningPathHandler = ({
    alias,
  }: IActionData): Observable<null> => this.courseService.getLearningPath(alias);

  public startOnboardingHandler = (): Observable<null> => this.commonService.startOnboarding();

  public openLessonHandler = ({ id }: IActionData): Observable<null> => this.lessonService.openLesson(id);

  public openCodeProjectHandler = ({
    id,
    returnUrl,
  }: IActionData): Observable<null> => this.materialService.openCodeProject(id, returnUrl);

  public openCodeRepoHandler = ({ id }: IActionData): Observable<null> => this.materialService.openCodeRepo(id);

  public initializeLessonPageHandler = (): Observable<null> => this.lessonService.initializeLessonPage();

  public submitSolutionsHandler = ({
    solutions,
    status,
  }: IActionData): Observable<null> => this.lessonService.submitSolutions(solutions, status);

  public toNextPageHandler = (): Observable<null> => this.lessonService.toNextPage();

  public checkAnswerHandler = (): Observable<null> => this.eventsService.onCheckAnswer();

  public tryAgainHandler = (): Observable<null> => this.lessonService.tryAgain();

  public toPreviousPageHandler = (): Observable<null> => this.lessonService.toPreviousPage();

  public closeLessonHandler = (): Observable<null> => this.lessonService.closeLesson();

  public deleteSessionDataHandler = (): Observable<null> => this.lessonService.deleteLessonSessionData();

  public deleteSessionFromBackend = ({
    lessonSessionQuitType,
  }: IActionData): Observable<null> => this.lessonService.deleteSessionFromBackend(
    lessonSessionQuitType,
  );

  public completeLessonHandler = (): Observable<null> => this.lessonService.completeLesson();

  public byPassLessonHandler = (): Observable<null> => this.lessonService.byPassLesson();

  public scrollIntoCurrentMaterialElementHandler = ({
    id,
  }: IActionData): Observable<null> => this.courseService.scrollIntoCurrentMaterialElement(id);

  public trySolveMaterialHandler = (): Observable<null> => this.materialService.trySolveMaterial();

  public tryAchieveStreakHandler = ({
    statusChanges,
    materialInfo,
  }: IActionData): Observable<IStreakDTO> => this.streakService.tryAchieveStreak(statusChanges, materialInfo);

  public defineLessonQuitPromptInfoHandler = (): Observable<null> => this.lessonService.defineLessonQuitPromptInfo();

  public clearLessonDataHandler = (): Observable<null> => this.lessonService.clearLessonData();

  public updateStatusesHandler = ({
    statusChanges,
  }: IActionData): Observable<null> => this.materialService.updateStatuses(statusChanges);

  public navigateToUserLoginHandler = (): Observable<null> => this.commonService.navigateToUserLogin();

  public navigateToProHandler = ({
    source,
    item,
    returnUrl,
  }: IActionData): Observable<null> => this.commonService.navigateToPro(source, item, returnUrl);

  public openUnlockQuizAnswerModalHandler = (): Observable<null> => this.lessonService.openUnlockQuizAnswerModal();

  public setHeartsDataHandler = ({
    hearts,
  }: IActionData): Observable<null> => this.heartsService.setHeartsData(hearts);

  public openHeartModalHandler = ({
    heartModalSource,
    callback,
  }: IActionData): Observable<null> => this.heartsService.openHeartModal(heartModalSource, callback);

  public setCourseCurrentItemsHandler = ({
    material,
  }: IActionData): Observable<null> => this.courseService.setCourseCurrentItems(material);

  public setPathCurrentItemsHandler = ({
    path,
    alias,
  }: IActionData): Observable<null> => this.courseService.setPathCurrentItems(path, alias);

  public onLessonNotFoundClickHandler = (): Observable<null> => this.eventsService.onLessonNotFoundClick();

  public onCourseNotFoundClickHandler = (): Observable<null> => this.eventsService.onCourseNotFoundClick();

  public updateDataAfterLoginHandler = (): Observable<null> => this.commonService.updateDataAfterLogin();

  public nextLessonCompleteScreenIterationHandler = (): Observable<null> => this.lessonService.nextLessonCompleteScreenIteration();

  public onLessonCompletedModalContinueClickHandler = (): Observable<null> => this.eventsService.onLessonCompletedModalContinueClick();

  public onFirstLessonCelebrationContinueClickHandler = (): Observable<null> => this.eventsService.onFirstLessonCelebrationContinueClick();

  public onStreakCelebrationScreenContinueClickHandler = (): Observable<null> => this.eventsService.onStreakCelebrationScreenContinueClick();

  public onCodeSnippetClickHandler = (): Observable<null> => this.eventsService.onCodeSnippetClick();

  public onImageClickHandler = ({
    imgUrl,
  }: IActionData): Observable<null> => this.eventsService.onImageClick(imgUrl);

  public onCloseNotAllowedFeatureModalClickHandler = (): Observable<null> => this.eventsService.onCloseNotAllowedFeatureModalClick();

  public onLandingPageCTAClickHandler = (): Observable<null> => this.eventsService.onLandingPageCTAClick();

  public onFreeHeartRefillCtaClickHandler = (): Observable<IKudosResponse> => this.eventsService.onFreeHeartRefillCtaClick();

  public onLeaderboardScreenContinueClickHandler = (): Observable<null> => this.eventsService.onLeaderboardScreenContinueClick();

  public onLeaderboardScreenMountHandler = (): Observable<null> => this.eventsService.onLeaderboardScreenMount();

  public onUnlockQuizAnswerMountHandler = (): Observable<null> => this.eventsService.onUnlockQuizAnswerMount();

  public onGoToLeaderboardClickHandler = (): Observable<null> => this.commonService.navigateToLeaderboard();

  public setBitLocationHandler = ({
    bitLocationType,
    bitLocationDetails,
    materialHeader,
  }: IActionData): Observable<null> => this.commonService.setBitLocation(
    bitLocationType,
    bitLocationDetails,
    materialHeader,
  );

  public decreaseBitsCountHandler = ({
    numberValue,
    price,
  }: IActionData): Observable<null> => this.commonService.decreaseBitsCount(numberValue, price);

  public solveMaterialCallbackHandler = ({
    statusChanges,
    solutions,
    materialInfo,
  }: IActionData): Observable<null> => this.materialService.solveMaterialCallback(
    { solutions, statusChanges, heartsState: null },
    materialInfo,
  );

  public makeMaterialChangesHandler = ({
    materialChanges,
  }: IActionData): Observable<null> => this.materialService.makeMaterialChanges(materialChanges);

  // Mount events

  public onCoursesMountHandler = (): Observable<null> => this.eventsService.onCoursesMount();

  public onCourseContainerMountHandler = ({
    alias,
    location,
  }: IActionData): Observable<null> => this.eventsService.onCourseContainerMount(alias, location);

  public onLearningPathContainerMountHandler = ({
    alias,
    location,
  }: IActionData): Observable<null> => this.eventsService.onLearningPathContainerMount(alias, location);

  public onCurrentModuleChildrenMountHandler = ({
    id,
  }: IActionData): Observable<null> => this.eventsService.onCurrentModuleChildrenMount(id);

  public onLessonContainerMountHandler = ({
    type,
    alias,
    materialId,
    lessonPageIndex,
    isSkipAhead,
  }: IActionData): Observable<null> => this.eventsService.onLessonContainerMount(
    type,
    alias,
    materialId,
    lessonPageIndex,
    isSkipAhead,
  );

  public onLessonPageMountHandler = (): Observable<null> => this.eventsService.onLessonPageMount();

  public onTheoryMountHandler = ({
    materialInfo,
  }: IActionData): Observable<null> => this.eventsService.onTheoryMount(materialInfo);

  public onTIYWidgetMountHandler = (): Observable<null> => this.eventsService.onTIYWidgetMount();

  public onRouteWithCourseContainerMountHandler = ({
    alias,
    locationUrl,
  }: IActionData): Observable<null> => this.eventsService.onRouteWithLEContainerMount(
    LearningExperienceType.Course,
    alias,
    locationUrl,
  );

  public onRouteWithLearningPathContainerMountHandler = ({
    alias,
    locationUrl,
  }: IActionData): Observable<null> => this.eventsService.onRouteWithLEContainerMount(
    LearningExperienceType.LearningPath,
    alias,
    locationUrl,
  );

  public onLessonPageIndexChangeHandler = ({
    id,
    lessonPageIndex,
  }: IActionData): Observable<null> => this.eventsService.onLessonPageIndexChange(id, lessonPageIndex);

  public onTIYMaterialMountHandler = ({
    alias,
    materialId,
  }: IActionData): Observable<null> => this.eventsService.onTIYMaterialMount(alias, materialId);

  public onCCMaterialMountHandler = ({
    alias,
    materialId,
  }: IActionData): Observable<null> => this.eventsService.onCCMaterialMount(alias, materialId);

  public onCRMaterialMountHandler = ({
    alias,
    materialId,
  }: IActionData): Observable<null> => this.eventsService.onCRMaterialMount(alias, materialId);

  public onLandingPageMountHandler = ({
    alias,
  }: IActionData): Observable<null> => this.eventsService.onLandingPageMount(alias);

  public onCourseMountHandler = ({
    materialInfo,
    isCourseAvailable,
  }: IActionData): Observable<null> => this.eventsService.onCourseMount(materialInfo, isCourseAvailable);

  public onLessonMountHandler = (): Observable<null> => this.eventsService.onLessonMount();

  public onSlLearnEngineMountHandler = (): Observable<null> => this.eventsService.onSlLearnEngineMount();

  public setLPCommentsHandler = ({
    comments,
  }: IActionData): Observable<null> => this.lessonService.setLPComments(comments);

  public onStreakCelebrationMountHandler = (): Observable<null> => this.eventsService.onStreakCelebrationMount();

  public OnFirstLessonCompleteCelebrationMountHandler = (): Observable<null> => this.eventsService.onFirstLessonCompleteCelebrationMount();

  public onLandingPageIsMountedHandler = ({
    alias,
  }: IActionData): Observable<null> => this.eventsService.onLandingPageIsMounted(alias);

  public onFreeHeartScreenMountHandler = (): Observable<null> => this.eventsService.onFreeHeartScreenMount();

  public onStreakGoalScreenMountHandler = (): Observable<null> => this.eventsService.onStreakGoalScreenMount();

  // Unmount events

  public onSlLearnEngineUnmountHandler = (): Observable<null> => this.eventsService.onSlLearnEngineUnmount();

  public onCourseUnmountHandler = (): Observable<null> => this.eventsService.onCourseUnmount();

  public onLessonContainerUnmountHandler = (): Observable<null> => this.eventsService.onLessonContainerUnmount();

  public onLessonPageUnmountHandler = (): Observable<null> => this.eventsService.onLessonPageUnmount();

  public onLeaderboardScreenUnmountHandler = (): Observable<null> => this.eventsService.onLeaderboardScreenUnmount();

  public onLessonQuitPromptUnmountHandler = (): Observable<null> => this.eventsService.onLessonQuitPromptUnmount();

  // Click events

  public onOpenTranslationModalClickHandler = (): Observable<null> => this.eventsService.onOpenTranslationModalClick();

  public onCourseClickHandler = ({
    alias,
  }: IActionData): Observable<null> => this.eventsService.onCourseClick(alias);

  public onPathClickHandler = ({ alias }: IActionData): Observable<null> => this.eventsService.onPathClick(alias);

  public onModuleClickHandler = ({ id }: IActionData): Observable<null> => this.eventsService.onModuleClick(id);

  public onLessonClickHandler = ({
    materialInfo,
  }: IActionData): Observable<null> => {
    if (!this.authService.isLoggedIn()) {
      return this.commonService.startLearning();
    }
    return this.eventsService.onLessonClick(materialInfo);
  };

  public onCloseLessonClickHandler = (): Observable<null> => this.eventsService.onCloseLessonClick();

  public onRunTIYClickHandler = ({
    id,
    status,
  }: IActionData): Observable<null> => {
    if (!this.authService.isLoggedIn()) {
      return this.commonService.startLearning();
    }
    return this.materialService.handleRunTIYClick(id, status);
  };

  public onRunExternalResourceClickHandler = ({
    externalResourceUrl,
    typeId,
    materialRelationId,
  }: IActionData): Observable<null> => {
    if (!this.authService.isLoggedIn()) {
      return this.commonService.startLearning();
    }
    return this.materialService.openExternalResource(
      externalResourceUrl,
      typeId,
      materialRelationId,
    );
  };

  public onRunExternalResourceCloseClickHandler = (): Observable<null> => this.materialService.closeExternalResource();

  public onCRPracticeClickHandler = ({
    materialInfo,
  }: IActionData): Observable<null> => {
    if (!this.authService.isLoggedIn()) {
      return this.commonService.startLearning();
    }
    return this.eventsService.onCRPracticeClick(materialInfo);
  };

  public onCCPracticeClickHandler = ({
    materialInfo,
  }: IActionData): Observable<null> => {
    if (!this.authService.isLoggedIn()) {
      return this.commonService.startLearning();
    }
    return this.materialService.openCodeCoach(materialInfo);
  };

  public onCPPracticeClickHandler = ({
    materialInfo,
    returnUrl,
  }: IActionData): Observable<null> => this.eventsService.onCPPracticeClick(materialInfo, returnUrl);

  public onLPCommentsClickHandler = (): Observable<null> => this.eventsService.onLPCommentsClick();

  public onCloseLPCommentsClickHandler = (): Observable<null> => this.eventsService.onCloseLPCommentsClick();

  public onHeartIconClickHandler = ({
    heartModalSource,
  }: IActionData): Observable<null> => this.eventsService.onHeartIconClick(heartModalSource);

  public onStartLearningClickHandler = (): Observable<null> => this.commonService.startLearning();

  public onCertificateCTAClickHandler = (): Observable<null> => this.eventsService.certificateCTAClick();

  public onCertificateCourseCompleteHandler = ({
    alias,
  }: IActionData): Observable<null> => this.eventsService.onCertificateCourseComplete(alias);

  public onLessonQuitPromptContinueClickHandler = (): Observable<null> => this.eventsService.onLessonQuitPromptContinueClick();

  public onLessonQuitPromptLeaveClickHandler = (): Observable<null> => this.eventsService.onLessonQuitPromptLeaveClick();

  public onLockedCardClickHandler = ({
    typeId,
  }: IActionData): Observable<null> => this.eventsService.onLockedCardClick(typeId);

  public onLockedCardContinueClickHandler = (): Observable<null> => this.eventsService.onLockedCardContinueClick();

  public onUnlockQuizAnswerButtonClickHandler = (): Observable<IMaterialSolutionDTO> => this.eventsService.onUnlockQuizAnswerButtonClick();

  public onUnlockQuizAnswerCloseClickHandler = (): Observable<null> => this.eventsService.onUnlockQuizAnswerClose();

  public onStreakGoalScreenButtonClickHandler = ({
    numberValue,
  }: IActionData): Observable<null> => this.eventsService.onStreakGoalScreenButtonClick(numberValue);

  public onOnboardingCtaClickHandler = (): Observable<null> => this.commonService.completeOnboarding();

  public openModalHandler = ({ modalId }: IActionData): Observable<null> => this.commonService.openModal(modalId);

  public closeModalHandler = ({ modalId }: IActionData): Observable<null> => this.commonService.closeModal(modalId);

  public onKodieIntroCloseHandler = (): Observable<null> => this.commonService.kodieIntroClose();

  public onKodieInNavigationAnimationEndHandler = (): Observable<null> => this.commonService.handleKodieInNavigationAnimationEnd();

  public onTranslateButtonClickHandler = ({
    locale,
  }: IActionData): Observable<null> => this.commonService.handleCourseTranslation(locale);

  public onSeeOriginalLanguageButtonClickHandler = (): Observable<null> => this.commonService.handleSeeOriginalLanguageButtonClick();

  public onSeeTranslatedLanguageButtonClickHandler = ({
    locale,
  }: IActionData): Observable<null> => this.commonService.handleSeeTranslatedLanguageButtonClick(
    locale,
  );

  public onCloseTranslationModalClickHandler = (): Observable<null> => {
    this.commonService.handleCloseTranslationModalClick();
    return of(null);
  };

  // Callback events

  public onUpdateLPCommentsCBHandler = ({
    lpCommentsCount,
  }: IActionData): Observable<null> => this.eventsService.onUpdateLPCommentsCB(lpCommentsCount);

  public onLandingSignupHandler = ({
    isNewRegistered,
  }: IActionData): Observable<null> => this.commonService.onLandingSignup(isNewRegistered);

  public expiredSessionIdErrorHandler = (): Observable<null> => {
    this.commonService.expiredSessionIdError();
    return of(null);
  };

  public onOnboardingCourseReadyHandler = ({
    material,
    learningExperience,
  }: IActionData): Observable<null> => {
    this.eventsService.onOnboardingCourseReady(material, learningExperience);
    return of(null);
  };

  public onOnboardingLessonReadyHandler = (): Observable<null> => {
    this.eventsService.onOnboardingLessonReady();
    return of(null);
  };

  public onOnboardingLoopIterationHandler = (): Observable<null> => {
    this.eventsService.onOnboardingLoopIteration();
    return of(null);
  };

  public onOnboardingCompleteHandler = (): Observable<null> => {
    this.lessonService.tryOpenFirstLesson();
    return of(null);
  };

  // Tracking

  public onTrackActionHandler = ({
    trackData,
  }: IActionData): Observable<null> => {
    this.trackingService[trackData.actionName](trackData);
    return of(null);
  };

  public onTrackFirstRefillImpressionHandler = (): Observable<null> => this.heartsService.trackFirstRefillImpression();

  public onTrackFirstRefillClickHandler = (): Observable<null> => this.heartsService.trackFirstRefillClick();

  public onTrackStreakCelebrationHandler = (): Observable<null> => {
    this.lessonService.trackStreakCelebrationImpression();
    return of(null);
  };

  public onTranslationWarningCtaClickHandler = ({
    isTranslated,
  }: IActionData): Observable<null> => {
    this.commonService.translationWarningCtaClick(isTranslated);

    return of(null);
  };

  public onUpdateSelectedLocaleHandler = ({
    locale,
    isFromCourseContainer,
  }: IActionData): Observable<null> => {
    this.commonService.updateSelectedLocale(locale, isFromCourseContainer);

    return of(null);
  };

  public onLessonCompletePaywallInitHandler = (): Observable<null> => {
    this.lessonService.openLessonCompletePaywall();
    return of(null);
  };
}
